/**
 * PAGE: LISTE PRODUITS (VERSION REFACTORISÉE)
 * Utilise useEntityList pour éliminer la duplication
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import {
  ColumnSelectorModal,
  CreateProductModal,
  FiltersModal,
  ImportCSVModal,
} from '../components/modals';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { EnterpriseTable } from '../components/tables';
import { CheckboxCell, ActionsCell, EditableInputCell } from '../components/tables/cells';
import { TableCell } from '../components/table';
import { BulkActionsBar, ConfirmDialog, ErrorState, LoadingState, toast } from '../components/ui';
import { useEntityList } from '../hooks/useEntityList';
import { useProducts } from '../hooks/useProducts';
import { useSupabase } from '../hooks/useSupabase';
import { usePermissions } from '../hooks/usePermissions';
import type { Product } from '../types/product';
import { PRODUCT_CONFIG, getProductDerivedFields } from '../config/entities/product.config';
import {
  generateCSVColumns,
  generateCSVTransformers,
  generateImportConfig,
  generateFilterConfigs,
  generateColumnOptions,
} from '../lib/entity-config';

export function ProductsListPage() {
  const supabase = useSupabase();
  const {
    data: products,
    loading,
    error,
    create,
    update,
    remove,
    removeMany,
    refreshProducts,
  } = useProducts();

  // Permissions utilisateur
  const {
    canCreateEntity,
    canUpdateEntity,
    canDeleteEntity,
    canImportData,
    canExportData,
    canDeleteBulk,
  } = usePermissions();

  // Tout est généré automatiquement depuis PRODUCT_CONFIG
  const filterConfigs = useMemo(() => generateFilterConfigs(PRODUCT_CONFIG), []);
  const csvColumns = useMemo(() => generateCSVColumns(PRODUCT_CONFIG), []);
  const csvTransformers = useMemo(() => generateCSVTransformers(PRODUCT_CONFIG), []);

  const importConfig = useMemo(
    () => ({
      ...generateImportConfig(PRODUCT_CONFIG),
      derivedFields: getProductDerivedFields(),
    }),
    []
  );

  // Hook générique - TOUTE la logique répétitive ici !
  const entity = useEntityList({
    data: products,
    loading,
    error,
    create,
    update,
    remove,
    removeMany,
    refresh: refreshProducts,
    entityName: PRODUCT_CONFIG.entityName,
    entityNamePlural: PRODUCT_CONFIG.entityNamePlural,
    searchableFields: ['name', 'start_date', 'end_date'],
    filterConfigs,
    csvFileName: 'produits.csv',
    csvColumns,
    csvTransformers,
    importConfig,
  });

  const { handleDeleteClick } = entity;

  // Mise à jour d'un produit (seule logique spécifique)
  const handleUpdateProduct = useCallback(
    async (id: string, field: keyof Product, value: string) => {
      if (!update) return;
      try {
        await update(id, { [field]: value });
        // Rafraîchir la liste pour récupérer les données mises à jour
        await refreshProducts();
        toast.success('Produit mis à jour');
      } catch {
        toast.error('Erreur lors de la mise à jour');
      }
    },
    [update, refreshProducts]
  );

  // Vérifier si des actions de masse sont possibles
  const hasAnyBulkAction = canDeleteBulk('product') || canExportData('product');
  const hasAnyRowAction = canUpdateEntity('product') || canDeleteEntity('product');

  // Définition des colonnes (seule partie spécifique)
  const columns: ColumnDef<Product>[] = useMemo(
    () => [
      // Colonne checkbox uniquement si actions de masse possibles
      ...(hasAnyBulkAction
        ? ([
            {
              id: 'select',
              size: 60,
              header: ({ table }: any) => (
                <CheckboxCell
                  row={
                    {
                      getIsSelected: () => table.getIsAllRowsSelected(),
                      toggleSelected: () => table.toggleAllRowsSelected(),
                    } as any
                  }
                />
              ),
              cell: ({ row }: any) => <CheckboxCell row={row} />,
            },
          ] as ColumnDef<Product>[])
        : []),
      {
        accessorKey: 'name',
        header: 'Produit',
        cell: ({ getValue }) => (
          <TableCell className="font-semibold">{getValue() as string}</TableCell>
        ),
        size: 200,
        enableSorting: true,
      },
      {
        accessorKey: 'start_date',
        header: 'Date de début',
        cell: ({ row, getValue }) => (
          <EditableInputCell
            value={getValue() as string}
            type="date"
            onSave={(newValue) => handleUpdateProduct(row.original.id, 'start_date', newValue)}
            readOnly={!canUpdateEntity('product')}
          />
        ),
        size: 200,
        enableSorting: true,
        sortingFn: 'datetime',
      },
      {
        accessorKey: 'end_date',
        header: 'Date de fin',
        cell: ({ row, getValue }) => (
          <EditableInputCell
            value={getValue() as string}
            type="date"
            onSave={(newValue) => handleUpdateProduct(row.original.id, 'end_date', newValue)}
            readOnly={!canUpdateEntity('product')}
          />
        ),
        size: 200,
        enableSorting: true,
        sortingFn: 'datetime',
      },
      // Colonne actions uniquement si des actions sont possibles
      ...(hasAnyRowAction
        ? ([
            {
              id: 'actions',
              header: 'Action',
              cell: ({ row }: any) => (
                <ActionsCell
                  onDelete={() => handleDeleteClick(row.original.id)}
                  canDelete={canDeleteEntity('product')}
                />
              ),
              size: 100,
            },
          ] as ColumnDef<Product>[])
        : []),
    ],
    [
      handleUpdateProduct,
      handleDeleteClick,
      canUpdateEntity,
      canDeleteEntity,
      hasAnyBulkAction,
      hasAnyRowAction,
    ]
  );

  // Colonnes disponibles pour la sélection (générées automatiquement)
  const columnOptions = useMemo(
    () => generateColumnOptions(PRODUCT_CONFIG, entity.table.columnVisibility),
    [entity.table.columnVisibility]
  );

  const handleColumnVisibilityApply = (
    newColumns: Array<{ id: string; label: string; visible?: boolean }>
  ) => {
    const newVisibility: Record<string, boolean> = {};
    newColumns.forEach((col) => {
      newVisibility[col.id] = col.visible !== false;
    });
    entity.table.setColumnVisibility(newVisibility);
  };

  // États de chargement et d'erreur
  if (entity.loading) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Produits' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <LoadingState message="Chargement des produits..." />
      </PageLayout>
    );
  }

  // Ne pas afficher l'erreur si le modal d'import est ouvert
  // (les erreurs d'import sont gérées dans le modal lui-même)
  if (entity.error && !entity.isImportModalOpen) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Produits' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <ErrorState
          title="Erreur lors du chargement des produits"
          message={entity.error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Produits' }]} />
      <div className="w-full h-px bg-zinc-200" />

      <PageContent
        title="Produits"
        description="Gérez les éditions d'événements (4L Trophy, TourQuad, etc.)"
        actions={
          <ActionButtons
            searchValue={entity.table.searchTerm}
            onSearchChange={entity.table.setSearchTerm}
            onDownload={canExportData('product') ? entity.handleExportCSV : undefined}
            onUpload={
              canImportData('product') ? () => entity.setIsImportModalOpen(true) : undefined
            }
            onFilter={() => entity.setIsFiltersModalOpen(true)}
            onColumns={() => entity.setIsColumnModalOpen(true)}
            onCreate={
              canCreateEntity('product') ? () => entity.setIsCreateModalOpen(true) : undefined
            }
            activeFiltersCount={entity.table.activeFilters.length}
          />
        }
        footer={
          <Pagination
            currentPage={entity.table.currentPage}
            totalPages={entity.table.totalPages}
            itemsPerPage={entity.table.itemsPerPage}
            totalItems={entity.table.totalItems}
            displayedItems={entity.table.displayedItemsCount}
            onPageChange={entity.table.setCurrentPage}
            onItemsPerPageChange={entity.table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <EnterpriseTable
          data={entity.table.filteredData}
          columns={columns}
          onRowSelectionChange={entity.table.setSelectedItems}
          columnVisibility={entity.table.columnVisibility}
          onColumnVisibilityChange={entity.table.setColumnVisibility}
          clearSelectionTrigger={entity.table.clearSelectionTrigger}
          pageSize={entity.table.itemsPerPage}
          currentPage={entity.table.currentPage}
          onPageChange={entity.table.setCurrentPage}
        />
      </PageContent>

      {/* Modales */}
      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={handleColumnVisibilityApply}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateProductModal
          isOpen={entity.isCreateModalOpen}
          onClose={() => entity.setIsCreateModalOpen(false)}
          onSubmit={async (data) => {
            await entity.handleCreate(data);
          }}
        />
      )}

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${PRODUCT_CONFIG.entityNamePlural}`
            : `Supprimer le ${PRODUCT_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Êtes-vous sûr de vouloir supprimer ${entity.table.selectedItems.length} ${PRODUCT_CONFIG.entityName}(s) ?`
            : `Êtes-vous sûr de vouloir supprimer ce ${PRODUCT_CONFIG.entityName} ?`
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={entity.confirmDelete}
        onCancel={() => entity.setDeleteConfirm({ open: false })}
      />

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data) => {
          // On doit wrapper l'appel pour capturer l'exception AVANT que useCRUD ne la transforme
          // car useCRUD retourne null en cas d'erreur, mais on a besoin de l'erreur originale
          const { data: product, error: supabaseError } = await supabase
            .from('products')
            .insert([data as any])
            .select()
            .single();

          if (supabaseError) {
            throw supabaseError;
          }

          return product;
        }}
        onUpdate={update}
        existingData={products}
        entityName={PRODUCT_CONFIG.entityName}
        entityNamePlural={PRODUCT_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      {/* Barre d'actions de masse */}
      {entity.table.selectedItems.length > 0 && (
        <BulkActionsBar
          count={entity.table.selectedItems.length}
          entityNamePlural={PRODUCT_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={false}
          canDelete={canDeleteBulk('product')}
          canExport={canExportData('product')}
        />
      )}
    </PageLayout>
  );
}
